import {
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from "@angular/core";
import { BookingConsultationState } from "../../interfaces/booking-consultation-state.interface";
import { ConsultationType } from "../../enums/consultation-type.enum";
import { ConsultationPaymentType } from "../../enums/payment-type.enum";
import { ActivatedRoute, Router } from "@angular/router";
import { LoggedUserService } from "@keus-template/auth";
import { CachedVisitsService } from "../cache/healthcare/cached-visits.service";
import { CachedDoctorsService } from "../cache/users/cached-doctors.service";
import { EMPTY, forkJoin, of, switchMap } from "rxjs";
import { CachedPsychologistsService } from "../cache";
import { PathwaysApiService } from "../api";
import { CompletePathwayDto } from "../../api/models";

/**
 * Configuration for consultation booking steps.
 * Defines the number of steps required for free vs paid consultations.
 */
const CONSULTATION_STEPS = {
  FREE: 2,  // Date selection -> Confirmation
  PAID: 4,  // Date -> Payment -> Confirmation -> Checkout
  VOLUNTARY: 5,  // Doctor -> Date -> Payment -> Confirmation -> Checkout
} as const;

@Injectable({
  providedIn: "root",
})
export class BookingConsultationStateService {
  /** The activate route instance  */
  private readonly _route: ActivatedRoute = inject(ActivatedRoute);

  /** The router instance */
  private readonly _router: Router = inject(Router);

  /** The pathways API service instance */
  private readonly _pathwayApiService: PathwaysApiService = inject(PathwaysApiService);

  /** The logged user service instance */
  private readonly _loggedUserService: LoggedUserService =
    inject(LoggedUserService);

  /** The cached visits service instance */
  private readonly _cachedVisitsService: CachedVisitsService =
    inject(CachedVisitsService);

  /** The cached doctors service instance */
  private readonly _cachedDoctorsService: CachedDoctorsService =
    inject(CachedDoctorsService);

  /** The cached psychologists service instance. */
  private readonly _cachedPsychologistsService: CachedPsychologistsService = inject(CachedPsychologistsService);

  /** The state of the booking */
  private readonly _state: WritableSignal<BookingConsultationState> =
    signal<BookingConsultationState>({
      type: null,
      currentStep: 1,
      totalSteps: 0,
    });

  /** Public readonly version of the state */
  public readonly state: Signal<BookingConsultationState> =
    this._state.asReadonly();

  /**
   * Initialize a new consultation session
   * Call this method in the OnInit
   */
  public initConsultation(): void {
    this._setPatientData();

    const consultationType = this._route.snapshot.queryParamMap.get(
      "type",
    ) as ConsultationType | null;
    const visitId = this._route.snapshot.queryParamMap.get("id");

    if (!consultationType) {
      this._handleNavigationError("consultation-type-missing");
      return;
    }

    // For types that don't require loading consultation data, calculate steps immediately
    if (consultationType === ConsultationType.VOLUNTARY) {
      this._calculateTotalStepsAndSetsTitleByType(consultationType);
      this._pathwayApiService.findActiveByPatientId$(this._loggedUserService.currentUser()?.id || "").pipe(
        switchMap((pathway) => {
          const activePhaseId = this._findActivePhaseId(pathway);

          this._state.update((s) => ({
            ...s,
            type: ConsultationType.VOLUNTARY,
            code: "VOLUNTARY",
            phaseId: activePhaseId,
            pathwayId: pathway?.createdAt ?? '',
          }));
          return EMPTY;
        })
      ).subscribe();
      return;
    }

    // For PRESCRIPTION and FIRST_VISIT types, steps will be calculated after loading consultation data
    // to check if it's free. For other types, calculate immediately.
    if (consultationType !== ConsultationType.PRESCRIPTION && consultationType !== ConsultationType.FIRST_VISIT) {
      this._calculateTotalStepsAndSetsTitleByType(consultationType);
    }

    if (
      visitId &&
      (consultationType === ConsultationType.FIRST_VISIT ||
        consultationType === ConsultationType.PRESCRIPTION ||
        consultationType === ConsultationType.REPROGRAMMING)
    ) {
      this._loadAndSetConsultation(visitId, consultationType);
      return;
    }

    this._handleNavigationError("consultation-id-missing");
  }

  /**
   * Return the identifier of the current active phase.
   * @param pathway the complete current pathway information.
   * @returns the pathway id.
   */
  private _findActivePhaseId(pathway: CompletePathwayDto): string {
    const activePhase = pathway.phases.find((phase) => phase.status === 'active');
    return activePhase ? activePhase.id : '';
  }

  /**
   * Calculates and sets the total steps and the title by the type of the consultation.
   * For PRESCRIPTION type consultations, payment steps are skipped if the consultation is free.
   * 
   * @param type - The type of the consultation
   * @param isFree - Whether the consultation is free (only relevant for PRESCRIPTION type)
   * @private
   */
  private _calculateTotalStepsAndSetsTitleByType(type: ConsultationType, isFree: boolean = false): void {
    // Define step configuration for each consultation type
    const stepConfig: Record<ConsultationType, { steps: number | ((isFree: boolean) => number); title: string }> = {
      [ConsultationType.FIRST_VISIT]: { 
        steps: (isFree: boolean) => isFree ? CONSULTATION_STEPS.FREE : CONSULTATION_STEPS.PAID,
        title: "Primo incontro conoscitivo" 
      },
      [ConsultationType.PRESCRIPTION]: { 
        steps: (isFree: boolean) => isFree ? CONSULTATION_STEPS.FREE : CONSULTATION_STEPS.PAID, 
        title: "Consulto" 
      },
      [ConsultationType.VOLUNTARY]: { steps: CONSULTATION_STEPS.VOLUNTARY, title: "Consulto" },
      [ConsultationType.REPROGRAMMING]: { steps: CONSULTATION_STEPS.FREE, title: "Consulto" },
    };

    const config = stepConfig[type];
    if (!config) {
      console.warn(`Unknown consultation type: ${type}`);
      return;
    }

    const totalSteps = typeof config.steps === 'function' ? config.steps(isFree) : config.steps;
    
    this._state.update((s) => ({
      ...s,
      totalSteps,
      title: config.title,
    }));
  }

  /**
   * Loads a consultation and updates the consultation state.
   * For PRESCRIPTION type consultations, determines if payment steps should be skipped
   * based on whether the consultation is free.
   * 
   * @param visitId - The ID of the visit/consultation to load
   * @param type - The type of consultation
   * @private
   */
  private _loadAndSetConsultation(
    visitId: string,
    type: ConsultationType,
  ): void {
    this._cachedVisitsService
      .findOne$(visitId)
      .pipe(
        switchMap((consultation) => {
          if (!consultation) {
            this._handleNavigationError("consultation-not-found");
            return EMPTY;
          }
          
          const userId = this._loggedUserService.currentUser()?.id || "";
          const doctorService = consultation.isProfessionalAPsycho 
            ? this._cachedPsychologistsService 
            : this._cachedDoctorsService;
          
          return forkJoin({
            consultation: of(consultation),
            doctor: doctorService.findOne$(consultation.doctorId),
            pathway: this._pathwayApiService.findActiveByPatientId$(userId),
          });
        }),
      )
      .subscribe({
        next: ({ consultation, doctor, pathway }) => {
          // Determine if consultation is free (price = 0 or explicitly marked as free)
          const isFree = this._isConsultationFree(consultation);
          
          // Calculate steps based on consultation type and payment requirement
          // For PRESCRIPTION and FIRST_VISIT types, steps depend on whether it's free
          if (type === ConsultationType.PRESCRIPTION || type === ConsultationType.FIRST_VISIT) {
            this._calculateTotalStepsAndSetsTitleByType(type, isFree);
          }
          
          // Update state with all consultation data
          this._state.update((s) => ({ ...s, type }));
          this._setDoctorData(doctor, consultation);
          this._setConsultationData(consultation);
          this._setPathwayData(pathway);
        },
        error: (error) => {
          console.error("Error loading consultation data:", error);
          this._handleNavigationError("server-error");
        },
      });
  }

  /**
   * Determines if a consultation is free based on price and isFree flag.
   * 
   * @param consultation - The consultation object to check
   * @returns true if the consultation is free, false otherwise
   * @private
   */
  private _isConsultationFree(consultation: any): boolean {
    return consultation.isFree === true || !consultation.price || consultation.price === 0;
  }

  /**
   * Handles navigation errors by redirecting to the consultations page with an error query param.
   * 
   * @param errorType - The type of error to display
   * @private
   */
  private _handleNavigationError(errorType: string): void {
    this._router.navigate(["/my-consultations"], {
      queryParams: { error: errorType },
    });
  }

  /**
   * Aggiorna lo stato con i dati del medico.
   * @param doctor dati del medico (può essere null)
   * @param fallback oggetto di fallback (consultation)
   */
  private _setDoctorData(doctor: any, fallback: any): void {
    this._state.update((s) => ({
      ...s,
      doctorId: doctor?.id ?? fallback.doctorId,
      doctorFirstName: doctor?.firstName ?? fallback.doctorFirstName,
      doctorLastName: doctor?.lastName ?? fallback.doctorLastName,
      doctorEmail: doctor?.email ?? fallback.doctorEmail,
      doctorPersonalEmail: doctor?.personalEmail ?? fallback.doctorPersonalEmail,
      doctorSpecialization: doctor?.specialization ?? "",
      doctorProfilePic: doctor?.profilePic ?? undefined,
    }));
  }

  /**
   * Aggiorna lo stato con i dati della consultazione.
   * @param consultation oggetto consultazione
   */
  private _setConsultationData(consultation: any): void {
    // Handle UNBOOKED placeholder value - treat it as no booking date
    const bookingDate = consultation.bookingDate === 'UNBOOKED' ? undefined : consultation.bookingDate;
    
    this._state.update((s) => ({
      ...s,
      bookingDate: bookingDate,
      consultationId: consultation.id,
      price: consultation.price,
      isFree: consultation.isFree || !consultation.price,
      title: consultation.title,
      calendarId: consultation.calendarId,
      visitId: consultation.id,
      code: consultation.code || "000",
      isProfessionalAPsycho: consultation.isProfessionalAPsycho,
      pathwayId: consultation.pathwayId,
      phaseId: consultation.phaseId,
      paymentStatus: consultation.paymentStatus,
    }));
  }

  /**
   * Sets the pathway data in the state.
   * @param pathway the pathway data to set in the state.
   */
  public _setPathwayData(pathway: CompletePathwayDto): void {
    const phaseId = "";

    this._state.update((s) => ({
      ...s,
      pathwayId: pathway.createdAt,
      phaseId: phaseId,
    }));
  }

  /**
   * Set the patient data
   */
  private _setPatientData(): void {
    const user = this._loggedUserService.currentUser();

    if (user) {
      this._state.update((s) => ({
        ...s,
        patientId: user.id,
        patientFirstName: user.firstName,
        patientLastName: user.lastName,
        patientEmail: user.email,
        stripeCustomerId: user.stripeId,
      }));
    } else {
      this._handleNavigationError("user-not-found");
    }
  }

  /**
   * Set doctor information
   * @param doctorId the id of the doctor
   * @param firstName the first name of the doctor
   * @param lastName the last name of the doctor
   * @param email the email of the doctor
   * @param specialization the specialization of the doctor
   * @param professionalType the type of professional (doctor/psychologist)
   * @param profilePic the profile picture URL (optional)
   * @param personalEmail the personal email of the doctor (optional)
   */
  public setDoctor(
    doctorId: string,
    firstName: string,
    lastName: string,
    email: string,
    specialization: string,
    professionalType: string,
    profilePic?: string,
    personalEmail?: string
  ): void {
    this._state.update((s) => ({
      ...s,
      doctorId,
      doctorFirstName: firstName,
      doctorLastName: lastName,
      doctorEmail: email,
      doctorPersonalEmail: personalEmail,
      doctorSpecialization: specialization,
      isProfessionalAPsycho: professionalType === "psychologist",
      doctorProfilePic: profilePic,
    }));
  }

  /**
   * Set only the date part of the visit, time is reset
   * @param dateOnly A Date object containing year, month, day
   */
  public setBookingDate(dateOnly: Date): void {
    const newDate = new Date(dateOnly);
    newDate.setHours(0, 0, 0, 0);
    this._state.update((s) => ({
      ...s,
      bookingDate: newDate.toISOString(),
    }));
  }

  /**
   * Set only the time of the existing visit date
   * @param time A string in "HH:mm" format
   */
  public setVisitTime(time: string): void {
    this._state.update((s) => {
      if (!s.bookingDate || s.bookingDate === 'UNBOOKED') return s;

      const [hours, minutes] = time.split(":").map(Number);
      const current = new Date(s.bookingDate);
      current.setHours(hours, minutes, 0, 0);

      return {
        ...s,
        bookingDate: current.toISOString(),
      };
    });
  }

  /**
   * Set payment information
   * @param payment the type of the payment
   * @param price the price of the consultation
   */
  public setPayment(payment: ConsultationPaymentType, price: number): void {
    this._state.update((s) => ({ ...s, payment, price }));
  }

  /**
   * Move to next step if possible
   */
  public nextStep(): void {
    this._state.update((s) => ({
      ...s,
      currentStep: Math.min(s.currentStep + 1, s.totalSteps),
    }));
  }

  /**
   * Move to previous step if possible
   */
  public prevStep(): void {
    this._state.update((s) => ({
      ...s,
      currentStep: Math.max(s.currentStep - 1, 1),
    }));
  }

  /**
   * Returns the key for the current step based on consultation type and step number.
   * For PRESCRIPTION consultations, payment steps are skipped if the consultation is free.
   * 
   * @returns The current step key identifier
   * @public
   */
  public getCurrentStepKey(): string {
    const state = this._state();
    const { type, currentStep, isFree, price } = state;

    if (!type) return "";

    // Define step mappings for each consultation type
    const stepMappings: Record<ConsultationType, Record<number, string>> = {
      [ConsultationType.VOLUNTARY]: {
        1: "doctor",
        2: "date",
        3: "payment",
        4: "confirmation",
        5: "checkout"
      },
      [ConsultationType.PRESCRIPTION]: this._getPrescriptionStepMapping(isFree || !price),
      [ConsultationType.FIRST_VISIT]: this._getFirstVisitStepMapping(isFree || !price),
      [ConsultationType.REPROGRAMMING]: {
        1: "date",
        2: "confirmation"
      }
    };

    return stepMappings[type]?.[currentStep] || "";
  }

  /**
   * Returns the step mapping for PRESCRIPTION type consultations.
   * 
   * @param isFree - Whether the consultation is free
   * @returns Step mapping object
   * @private
   */
  private _getPrescriptionStepMapping(isFree: boolean): Record<number, string> {
    // Free consultations: 2 steps (date selection and confirmation)
    if (isFree) {
      return {
        1: "date",
        2: "confirmation"
      };
    }
    
    // Paid consultations: 4 steps (date, payment, confirmation, checkout)
    return {
      1: "date",
      2: "payment",
      3: "confirmation",
      4: "checkout"
    };
  }

  /**
   * Returns the step mapping for FIRST_VISIT type consultations.
   * 
   * @param isFree - Whether the consultation is free
   * @returns Step mapping object
   * @private
   */
  private _getFirstVisitStepMapping(isFree: boolean): Record<number, string> {
    // Free consultations: 2 steps (date selection and confirmation)
    if (isFree) {
      return {
        1: "date",
        2: "confirmation"
      };
    }
    
    // Paid consultations: 4 steps (date, payment, confirmation, checkout)
    // Same as PRESCRIPTION when paid, no doctor selection needed
    return {
      1: "date",
      2: "payment",
      3: "confirmation",
      4: "checkout"
    };
  }

  /**
   * Checks if the current step has valid data
   * @returns the validity of the current step
   */
  public isCurrentStepValid(): boolean {
    const stepKey = this.getCurrentStepKey();
    const state = this._state();

    switch (stepKey) {
      case "doctor":
        return !!state.doctorId;
      case "date":
        const hasDate = !!state.bookingDate;
        const hasTime = this.hasSettedHours();
        return hasDate && hasTime;
      case "payment":
        return !!state.payment;
      case "confirmation":
        return true;
      default:
        return false;
    }
  }

  /**
   * Check if the user has setter hours
   * @returns true if has setted hours, false otherwise
   */
  public hasSettedHours(): boolean {
    const bookingDate = this._state().bookingDate;
    if (!bookingDate || bookingDate === 'UNBOOKED') return false;

    const date = new Date(bookingDate);
    return !(date.getHours() === 0 && date.getMinutes() === 0);
  }

  /**
   * Determines whether to create or update a Google Calendar event.
   * 
   * Logic:
   * - No visitId: VOLUNTARY consultation → CREATE (creates both visit and calendar event)
   * - visitId but no calendarId: First booking of existing visit → CREATE (only calendar event)
   * - Both visitId and calendarId: Rescheduling → UPDATE (modifies existing calendar event)
   *
   * @returns "create" | "update" - the action type for Google Calendar API
   */
  public getActionType(): "create" | "update" {
    const state = this._state();
    
    // If no visitId, it's a voluntary consultation - need to create both visit and event
    if (!state.visitId) {
      return "create";
    }
    
    // If we have visitId but no calendarId, it's the first booking for an existing visit
    // The visit already exists in the system, we just need to CREATE the Google Calendar event
    if (!state.calendarId) {
      return "create";
    }
    
    // If we have both visitId and calendarId, it's an update (rescheduling an existing booking)
    return "update";
  }

  /**
   * Set the first booking consultation flag
   * @param isFirstBooking whether this is the first booking consultation during registration
   */
  public setFirstBookingConsultation(isFirstBooking: boolean): void {
    this._state.update((s) => ({
      ...s,
      firstBookingConsultation: isFirstBooking,
    }));
  }

  /**
   * Reset consultation state
   */
  public resetConsultation(): void {
    this._state.set({
      type: null,
      currentStep: 1,
      totalSteps: 0,
    });
  }
}
